import { FunctionComponent, ReactNode } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import type { GlobalDataContainerQuery } from "@framework/__generated__/relay/GlobalDataContainerQuery.graphql";
import {
  type GlobalDataSEO,
  getSiteSettingsSEO,
} from "@framework/seo/cms-seo.utils";
import {
  type Banner,
  type SEO,
  type SiteLogo,
  type NavItem,
} from "@voxmedia/duet-context/context/global-data-context";
import GlobalDataProvider from "@voxmedia/duet-context/providers/GlobalDataProvider";
import { getThumbnailDimensions } from "@voxmedia/duet-utils/images";

interface Props {
  children?: ReactNode;
  overrides?: {
    mainNavItems?: NavItem[];
    footerItems?: NavItem[];
    siteSettings?: SiteSettingsOverride;
  };
}

export type SiteSettingsOverride =
  GlobalDataContainerQuery["response"]["siteSettings"];

const GlobalDataContainer: FunctionComponent<Props> = ({
  children,
  overrides,
}) => {
  const {
    breakingNewsBanner,
    wikiBanner,
    siteSettings,
    newsletters,
    mainNav,
    footerNav,
  } = useLazyLoadQuery<GlobalDataContainerQuery>(
    graphql`
      query GlobalDataContainerQuery {
        breakingNewsBanner {
          buttonText
          buttonUrl
          content {
            html
          }
          heading
        }
        wikiBanner {
          buttonText
          buttonUrl
          content {
            html
          }
          heading
          showOnHomepage
        }
        siteSettings {
          siteDomain
          siteSlug
          siteTitle
          siteTagline
          placeholderImage {
            thumbnails {
              horizontal {
                url
              }
              vertical {
                url
              }
              square {
                url
              }
              social {
                url
              }
            }
          }
          siteLogo {
            originalWidth
            originalHeight
            thumbnails {
              original {
                url
                cropCoordinates {
                  height
                  width
                }
              }
              social {
                url
                cropCoordinates {
                  height
                  width
                }
              }
            }
          }
          socialMediaLinks {
            platformName
            url
          }
          seo {
            defaultSchema {
              title
              facebookAppId
              googleSiteVerification
            }
            newsMediaOrganizationSchema {
              description
              foundingDate
              founders
              knowsAbout
              diversityPolicyUrl
              ethicsPolicyUrl
              mastheadUrl
              wikipediaUrl
              linkedInUrl
            }
          }
        }
        newsletters {
          title
          slug
          listId
          default
          promoText {
            html
          }
          frequency
          image {
            thumbnails {
              original {
                url
              }
            }
          }
        }
        mainNav: navMenu(location: MAIN) {
          menuItems {
            nodes {
              label
              url
              menuItems {
                label
                url
              }
            }
          }
        }
        footerNav: navMenu(location: FOOTER) {
          menuItems {
            nodes {
              label
              url
              menuItems {
                label
                url
              }
            }
          }
        }
      }
    `,
    {},
  );

  const {
    placeholderImage,
    siteTagline,
    siteLogo: siteLogoData,
    seo: seoData,
    ...restSiteSettings
  } = siteSettings;

  // top-level merging of overrides into site settings
  const resolvedSiteSettings = {
    ...restSiteSettings,
    ...(overrides?.siteSettings ?? {}),
  };

  const resolvedSiteTagline =
    overrides &&
    "siteSettings" in overrides &&
    overrides.siteSettings &&
    "siteTagline" in overrides.siteSettings
      ? overrides.siteSettings.siteTagline
      : siteTagline;

  let siteLogo: SiteLogo | null = null;
  const resolvedSiteLogoData =
    overrides &&
    "siteSettings" in overrides &&
    overrides.siteSettings &&
    "siteLogo" in overrides.siteSettings
      ? overrides.siteSettings.siteLogo
      : siteLogoData;
  if (resolvedSiteLogoData) {
    siteLogo = {
      social: {
        url: resolvedSiteLogoData.thumbnails.social.url,
        ...getThumbnailDimensions(
          resolvedSiteLogoData,
          resolvedSiteLogoData.thumbnails.social,
        ),
      },
      original: {
        url: resolvedSiteLogoData.thumbnails.original.url,
        ...getThumbnailDimensions(
          resolvedSiteLogoData,
          resolvedSiteLogoData.thumbnails.original,
        ),
      },
    };
  }

  // TODO: `dek` or whatever we call it should be in the CMS.
  let overrideWikiBanner: Banner | null = null;
  if (wikiBanner) {
    overrideWikiBanner = { ...wikiBanner };
    overrideWikiBanner.dek =
      "We rely on readers like you to fund our journalism. <strong>Will you support our work and become a Vox Member today?</strong>";
  }

  let seo: SEO | null = null;
  const baseSeo: GlobalDataSEO = {
    defaultSchema: seoData?.defaultSchema ?? null,
    newsMediaOrganizationSchema: seoData?.newsMediaOrganizationSchema ?? null,
  };
  const overridesSeo = overrides?.siteSettings?.seo;
  const resolvedSeoData: GlobalDataSEO = {
    defaultSchema:
      (overridesSeo?.defaultSchema as
        | GlobalDataSEO["defaultSchema"]
        | undefined) ?? baseSeo.defaultSchema,
    newsMediaOrganizationSchema:
      (overridesSeo?.newsMediaOrganizationSchema as
        | GlobalDataSEO["newsMediaOrganizationSchema"]
        | undefined) ?? baseSeo.newsMediaOrganizationSchema,
  };
  seo = getSiteSettingsSEO(resolvedSeoData);

  const mapMenuItems = (nav?: typeof mainNav): NavItem[] | null => {
    if (!nav?.menuItems?.nodes) {
      return null;
    }

    return nav.menuItems.nodes.map((item) => ({
      title: item.label,
      url: item.url || "",
      subItems: item.menuItems.map((subItem) => ({
        title: subItem.label,
        url: subItem.url || "",
      })),
    }));
  };

  const resolvedMainNav =
    overrides && "mainNavItems" in overrides && overrides.mainNavItems
      ? overrides.mainNavItems
      : mapMenuItems(mainNav);

  const resolvedFooterNav =
    overrides && "footerItems" in overrides && overrides.footerItems
      ? overrides.footerItems
      : mapMenuItems(footerNav);

  return (
    <GlobalDataProvider
      globalData={{
        breakingNewsBanner,
        wikiBanner: overrideWikiBanner,
        siteSettings: {
          ...resolvedSiteSettings,
          siteTagline: resolvedSiteTagline || undefined,
          placeholderImageUrl: {
            horizontal: placeholderImage?.thumbnails.horizontal.url || "",
            vertical: placeholderImage?.thumbnails.vertical.url || "",
            square: placeholderImage?.thumbnails.square.url || "",
            social: placeholderImage?.thumbnails.social.url || "",
          },
          siteLogo,
          seo,
        },
        newsletters,
        mainNav: resolvedMainNav,
        footerNav: resolvedFooterNav,
      }}
    >
      {children}
    </GlobalDataProvider>
  );
};

export default GlobalDataContainer;
